import moment from "moment";

/**
 * Single source of truth for opening hours, mirrored from api/src/lib/cron.ts.
 * dow: 0=Sun, 1=Mon, ..., 6=Sat. Times are local Bangkok wall-clock (24h, in minutes).
 */
export type Channel = "cafe" | "bar" | "dessert";

export interface Window {
  channel: Channel;
  dow: number;
  openMin: number;
  closeMin: number;
}

export const WINDOWS: Window[] = [
  // Cafe — Mon–Thu 09:00–17:00
  { channel: "cafe",    dow: 1, openMin:  9 * 60, closeMin: 17 * 60 },
  { channel: "cafe",    dow: 2, openMin:  9 * 60, closeMin: 17 * 60 },
  { channel: "cafe",    dow: 3, openMin:  9 * 60, closeMin: 17 * 60 },
  { channel: "cafe",    dow: 4, openMin:  9 * 60, closeMin: 17 * 60 },
  // Cafe — Fri 09:00–12:00
  { channel: "cafe",    dow: 5, openMin:  9 * 60, closeMin: 12 * 60 },
  // Dessert — Mon–Fri 12:00–16:00
  { channel: "dessert", dow: 1, openMin: 12 * 60, closeMin: 16 * 60 },
  { channel: "dessert", dow: 2, openMin: 12 * 60, closeMin: 16 * 60 },
  { channel: "dessert", dow: 3, openMin: 12 * 60, closeMin: 16 * 60 },
  { channel: "dessert", dow: 4, openMin: 12 * 60, closeMin: 16 * 60 },
  { channel: "dessert", dow: 5, openMin: 12 * 60, closeMin: 16 * 60 },
];

export const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function fmtMin(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Whether `channel` is open right now per the schedule. */
export function isChannelOpen(channel: Channel, now = new Date()): boolean {
  const m = moment(now);
  const dow = m.day();
  const minutes = m.hour() * 60 + m.minute();
  return WINDOWS.some(
    (w) => w.channel === channel && w.dow === dow && minutes >= w.openMin && minutes < w.closeMin,
  );
}

/** Next opening time for `channel`, or null if not scheduled within 8 days. */
export function nextOpenFor(channel: Channel, now = new Date()): moment.Moment | null {
  for (let dayOffset = 0; dayOffset < 8; dayOffset++) {
    const d = moment(now).add(dayOffset, "days");
    const dow = d.day();
    for (const w of WINDOWS) {
      if (w.channel !== channel || w.dow !== dow) continue;
      const when = d.clone().startOf("day").add(w.openMin, "minutes");
      if (when.isAfter(now)) return when;
    }
  }
  return null;
}

/** Find the next opening across ALL channels — used by the close-all page. */
export function nextOpenAny(now = new Date()): { when: moment.Moment; window: Window } | null {
  for (let dayOffset = 0; dayOffset < 8; dayOffset++) {
    const d = moment(now).add(dayOffset, "days");
    const dow = d.day();
    const candidates = WINDOWS.filter((w) => w.dow === dow);
    for (const w of candidates) {
      const when = d.clone().startOf("day").add(w.openMin, "minutes");
      if (when.isAfter(now)) return { when, window: w };
    }
  }
  return null;
}

/**
 * Group windows for a single channel into "Mon–Thu 09:00–17:00" rows.
 * Used by close-all and menu pages to render the schedule.
 */
/** ISO weekday (1=Mon ... 7=Sun) for a Date. */
export function isoDay(d: Date): number {
  const dow = d.getDay();
  return dow === 0 ? 7 : dow;
}

const DAY_LABELS_ISO = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Compress sorted ISO day numbers (1..7) to "Mon–Fri", "Mon, Wed", etc. */
export function formatIsoDays(days: number[] | null | undefined): string {
  if (!days || days.length === 0 || days.length === 7) return "Every day";
  const sorted = [...new Set(days)].sort((a, b) => a - b);
  const runs: string[] = [];
  let s: number | null = null, e: number | null = null;
  const flush = () => {
    if (s === null) return;
    runs.push(s === e ? DAY_LABELS_ISO[s - 1] : `${DAY_LABELS_ISO[s - 1]}–${DAY_LABELS_ISO[e! - 1]}`);
    s = e = null;
  };
  for (const d of sorted) {
    if (s === null) { s = e = d; continue; }
    if (d === e! + 1) { e = d; continue; }
    flush();
    s = e = d;
  }
  flush();
  return runs.join(", ");
}

export interface MenuAvailability {
  availableDays?: number[] | null;
  availableFromMin?: number | null;
  availableToMin?: number | null;
}

/** True if this menu is currently within its (per-item) day/time window. */
export function isMenuAvailableNow(menu: MenuAvailability, now = new Date()): boolean {
  const day = isoDay(now);
  const minutes = now.getHours() * 60 + now.getMinutes();
  const days = menu.availableDays;
  if (days && days.length > 0 && !days.includes(day)) return false;
  const from = menu.availableFromMin;
  const to = menu.availableToMin;
  if (from != null && minutes < from) return false;
  if (to != null && minutes >= to) return false;
  return true;
}

/** Human-readable summary of a menu's availability window, e.g. "Mon, Wed · 12:00–16:00". */
export function summarizeMenuAvailability(menu: MenuAvailability): string {
  const days = formatIsoDays(menu.availableDays);
  const hasTime = menu.availableFromMin != null || menu.availableToMin != null;
  if (!hasTime) return days;
  const f = menu.availableFromMin != null ? fmtMin(menu.availableFromMin) : "—";
  const t = menu.availableToMin != null ? fmtMin(menu.availableToMin) : "—";
  return `${days} · ${f}–${t}`;
}

export function scheduleRows(channel: Channel): Array<{ days: string; time: string }> {
  const groups: Record<string, { openMin: number; closeMin: number; days: number[] }> = {};
  for (const w of WINDOWS) {
    if (w.channel !== channel) continue;
    const k = `${w.openMin}|${w.closeMin}`;
    if (!groups[k]) groups[k] = { openMin: w.openMin, closeMin: w.closeMin, days: [] };
    groups[k].days.push(w.dow);
  }
  return Object.values(groups).map((g) => {
    g.days.sort();
    const runs: string[] = [];
    let runStart: number | null = null, runEnd: number | null = null;
    const flush = () => {
      if (runStart === null) return;
      runs.push(runStart === runEnd
        ? DAY_LABELS[runStart]
        : `${DAY_LABELS[runStart]}–${DAY_LABELS[runEnd!]}`);
      runStart = runEnd = null;
    };
    for (const d of g.days) {
      if (runStart === null) { runStart = runEnd = d; continue; }
      if (d === runEnd! + 1) { runEnd = d; continue; }
      flush();
      runStart = runEnd = d;
    }
    flush();
    return { days: runs.join(", "), time: `${fmtMin(g.openMin)} – ${fmtMin(g.closeMin)}` };
  });
}
